import { styled } from "@mui/system";
import { filter, identity } from "ramda";
import type { ReactNode } from "react";
import { useState } from "react";

import useIntersectionObserver from "hooks/use-intersection-observer";

import type { DialogContentProps as MuiDialogContentProps } from "components/core/mui";
import { Box, DialogContent as MuiDialogContent } from "components/core/mui";

type DialogContentProps = {
  [key: string]: unknown;
  children?: ReactNode;
};

const TOP_SHADOW = "inset 0 7px 4px -4px rgba(0, 0, 0, 0.08)";
const BOTTOM_SHADOW = "inset 0 -7px 4px -4px rgba(0, 0, 0, 0.08)";

type StyledDialogContentProps = MuiDialogContentProps & {
  bottomShadow?: string | false;
  topShadow?: string | false;
};

const StyledDialogContent = styled(MuiDialogContent, {
  shouldForwardProp: (prop) => prop !== "topShadow" && prop !== "bottomShadow",
})<StyledDialogContentProps>(({ theme, topShadow, bottomShadow }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(3),
  },
  "&:has(+ .MuiDialogActions-root)": {
    boxShadow: bottomShadow || "none",
  },
  ".MuiDialogTitle-root ~ &": {
    boxShadow: topShadow || "none",
    ...(topShadow && {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      borderTop: `1px solid ${theme.palette.neutrals.lighterGrey}`,
    }),
  },
  ".MuiDialogTitle-root ~ &:has(+ .MuiDialogActions-root)": {
    // @ts-expect-error - typing here doesn't work but runtime does
    boxShadow: filter(identity, [topShadow, bottomShadow]).join(","),
  },
}));

/**
 * This is a small wrapper around `DialogContent` that adds shadows to show when the content can
 * be scrolled up or down. If the content is small enough that it never needs to scroll, then this
 * will behave exactly like the standard `DialogContent`.
 *
 * It also determines if the shadows should show based on whether there is a title or actions
 * before or after the content (i.e. only show a shadow is there are "stickied" elements around
 * the content).
 *
 * #### Links
 *
 * - [https://mui.com/api/dialog-content/](https://mui.com/api/dialog-content/)
 */
/* eslint-disable react/prop-types */
const DialogContent: React.FC<DialogContentProps> = ({ children, ...rest }) => {
  const [hasTopShadow, setTopShadow] = useState(false);
  const [hasBottomShadow, setBottomShadow] = useState(false);

  const [rootElement, rootRef] = useState<HTMLElement | null>(null);

  const [topRef] = useIntersectionObserver(
    ([entry]) => {
      setTopShadow(!entry.isIntersecting);
    },
    { root: rootElement },
  );

  const [bottomRef] = useIntersectionObserver(
    ([entry]) => {
      setBottomShadow(!entry.isIntersecting);
    },
    { root: rootElement },
  );

  const topShadow = hasTopShadow && TOP_SHADOW;
  const bottomShadow = hasBottomShadow && BOTTOM_SHADOW;

  return (
    <StyledDialogContent
      ref={rootRef}
      bottomShadow={bottomShadow}
      topShadow={topShadow}
      {...rest}
    >
      <div ref={topRef} />
      {children}
      <Box ref={bottomRef} sx={{ position: "relative", bottom: 1 }} />
    </StyledDialogContent>
  );
};

export default DialogContent;
